/**
 * Magic.Lap spark motif — two concentric circles, the canonical brand mark.
 * Outer ring: stroke-only at 32% opacity. Inner circle: filled currentColor.
 * Matches brand-reference/magic_lap_design_system.md §"The Brand Mark".
 *
 * Usage:
 *   <Spark variant="inline" />          — inline wordmark separator (0.5em)
 *   <Spark variant="divider" />         — section divider (20×20)
 *   <Spark variant="empty-state" />     — empty state icon (64×64, 40% opacity)
 *   <Spark variant="loader" />          — pulsing loader (40×40, animated)
 *   <Spark className="w-6 h-6" />       — arbitrary size
 */

import type { SVGAttributes } from 'react';

type SparkVariant = 'inline' | 'divider' | 'empty-state' | 'loader';

interface SparkProps extends SVGAttributes<SVGSVGElement> {
  variant?: SparkVariant;
}

const VARIANT_CLASS: Record<SparkVariant, string> = {
  'inline':      'spark-inline',
  'divider':     'spark-divider',
  'empty-state': 'spark-empty-state',
  'loader':      'spark-loader',
};

export function Spark({ variant, className = '', ...rest }: SparkProps) {
  const variantClass = variant ? VARIANT_CLASS[variant] : '';
  const cls = [variantClass, className].filter(Boolean).join(' ');

  return (
    <svg
      viewBox="0 0 100 100"
      fill="currentColor"
      aria-hidden="true"
      className={cls || undefined}
      style={{ overflow: 'visible' }}
      {...rest}
    >
      {/* Outer halo ring — 32% opacity stroke, no fill */}
      <circle cx="50" cy="50" r="38" fill="none" stroke="currentColor" strokeWidth="3" opacity="0.32" />
      {/* Inner filled circle */}
      <circle cx="50" cy="50" r="18" />
    </svg>
  );
}
